import * as React from "react";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";

import "./MonthlyLeadersTab.css";
import { BASE_API_URL } from "utils/constants";

import { getUserData } from "Redux/UserDetails";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const LeadersListWithPagination = React.memo(
  function LeadersListWithPagination({
    leaders = [],
    tabKey,
    loading,
    loadError,
    roleFilter,
    categoryFilter,
    industryFilter,
    capabilityFilter,
    muSmartFilter,
    getPagedData,
    handlePageChange,
    handleRowsPerPageChange,
  }) {
    if (loading) {
      return (
        <div
          style={{ display: "flex", justifyContent: "center", padding: "40px" }}
        >
          <CircularProgress sx={{ color: "#a200ff" }} />
        </div>
      );
    }

    if (loadError) {
      return (
        <p
          style={{
            margin: 0,
            color: "#ffffffb0",
            textAlign: "center",
            padding: "24px",
          }}
        >
          {loadError}
        </p>
      );
    }

    const filteredLeaders = leaders
      .filter((item) =>
        roleFilter === "All" ? true : item.role === roleFilter,
      )
      .filter((item) =>
        categoryFilter === "All" ? true : item.category === categoryFilter,
      )
      .filter((item) =>
        industryFilter === "All" ? true : item.industry === industryFilter,
      )
      .filter((item) =>
        capabilityFilter === "All"
          ? true
          : item.capability === capabilityFilter,
      )
      .filter((item) =>
        muSmartFilter === "All" ? true : item.muSmart === muSmartFilter,
      );

    const { sliced, total, start, end, totalPages, page, rows } = getPagedData(
      filteredLeaders,
      tabKey,
    );

    return (
      <>
        <div className="leaders-medals-wrap">
          {total === 0 ? (
            <p style={{ margin: 0, color: "#ffffff71" }}>
              No users found for {tabKey} badge.
            </p>
          ) : (
            sliced.map((item) => {
              const initials = item.name
                ? item.name
                    .split(" ")
                    .filter(Boolean)
                    .map((w) => w.charAt(0))
                    .join("")
                : item.enterpriseId
                    ?.split(".")
                    .filter(Boolean)
                    .map((w) => w.charAt(0))
                    .join("")
                    .toUpperCase();

              return (
                <div key={item.enterpriseId} className="leaders-medals-row">
                  <div className="leaders-medals-box">
                    <div className="top-leaders-content-wrap">
                      <div className="top-leaders-content-box">
                        <h3>{initials}</h3>
                      </div>

                      <div className="top-leaders-content-box">
                        <h4>{item.enterpriseId}</h4>
                        {item.muSmart ? <p>{item.muSmart}</p> : <p>-</p>}
                      </div>
                    </div>

                    <div className="top-leaders-numbers-wrap">
                      <div className="top-leaders-numbers-box">
                        <p>
                          <span>{item.totalCoursesCompleted}</span> <br />{" "}
                          Trainings
                        </p>
                      </div>
                      <div className="top-leaders-numbers-box">
                        <p>
                          <span>{item.totalPoints}</span> <br /> Points
                        </p>
                      </div>
                      <div className="top-leaders-numbers-box">
                        <p>
                          <span>{item.totalBadgesEarned}</span> <br /> Badges
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {total > 0 && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            sx={{ mt: 2 }}
            className="leaders-pagination-row"
          >
            <Typography variant="body2" className="pagination-Text">
              Showing <b>{start + 1}</b>-<b>{Math.min(end, total)}</b> of{" "}
              <b>{total}</b>
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center">
              <FormControl
                size="small"
                sx={{
                  minWidth: 130,
                  "& .MuiInputLabel-root": { color: "white" },
                  "& .MuiOutlinedInput-root": {
                    color: "white",
                    "& fieldset": { borderColor: "white" },
                    "&:hover fieldset": { borderColor: "white" },
                    "&.Mui-focused fieldset": { borderColor: "white" },
                  },
                  "& .MuiSvgIcon-root": { color: "white" },
                }}
              >
                <InputLabel id={`${tabKey}-rows-label`}>Rows</InputLabel>

                <Select
                  labelId={`${tabKey}-rows-label`}
                  value={rows}
                  label="Rows"
                  onChange={(e) =>
                    handleRowsPerPageChange(tabKey, Number(e.target.value))
                  }
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: "#333",
                        color: "white",
                      },
                    },
                  }}
                >
                  {[4, 6, 8, 10, 12].map((n) => (
                    <MenuItem key={n} value={n} sx={{ color: "white" }}>
                      {n} / page
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, newPage) => handlePageChange(tabKey, newPage)}
                sx={{
                  "& .MuiPaginationItem-root": {
                    fontSize: "1rem",
                  },
                }}
                color="primary"
                shape="rounded"
                siblingCount={1}
                boundaryCount={1}
              />
            </Stack>
          </Stack>
        )}
      </>
    );
  },
);

LeadersListWithPagination.propTypes = {
  leaders: PropTypes.array,
  tabKey: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  loadError: PropTypes.string,
  roleFilter: PropTypes.string.isRequired,
  categoryFilter: PropTypes.string.isRequired,
  industryFilter: PropTypes.string.isRequired,
  capabilityFilter: PropTypes.string.isRequired,
  muSmartFilter: PropTypes.string.isRequired,
  getPagedData: PropTypes.func.isRequired,
  handlePageChange: PropTypes.func.isRequired,
  handleRowsPerPageChange: PropTypes.func.isRequired,
};

export default function BasicTabs({ enterpriseIds = [], onTop3MUs }) {
  const [value, setValue] = React.useState(0);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [diamondLeader, setDiamondLeader] = useState([]);
  const [platinumLeader, setPlatinumLeader] = useState([]);
  const [goldLeader, setGoldLeader] = useState([]);
  const [silverLeader, setSilverLeader] = useState([]);
  const [bronzeLeader, setBronzeLeader] = useState([]);

  const [roleFilter, setRoleFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [industryFilter, setIndustryFilter] = useState("All");
  const [capabilityFilter, setCapabilityFilter] = useState("All");
  const [muSmartFilter, setMuSmartFilter] = useState("All");

  const userData = useSelector((state) => state.userDetails.userData[0]);

  const TAB_KEYS = useMemo(
    () => ["Diamond", "Platinum", "Gold", "Silver", "Bronze"],
    [],
  );

  const makeDefaultByTab = useCallback(
    (val) =>
      TAB_KEYS.reduce((acc, key) => {
        acc[key] = val;
        return acc;
      }, {}),
    [TAB_KEYS],
  );

  const [pageByTab, setPageByTab] = useState(() => makeDefaultByTab(1));
  const [rowsPerPageByTab, setRowsPerPageByTab] = useState(() =>
    makeDefaultByTab(6),
  );

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handlePageChange = useCallback((tabKey, newPage) => {
    setPageByTab((prev) => ({ ...prev, [tabKey]: newPage }));
  }, []);

  const handleRowsPerPageChange = useCallback((tabKey, newRows) => {
    setRowsPerPageByTab((prev) => ({ ...prev, [tabKey]: newRows }));
    setPageByTab((prev) => ({ ...prev, [tabKey]: 1 }));
  }, []);

  const getPagedData = useCallback(
    (items, tabKey) => {
      const page = pageByTab[tabKey] || 1;
      const rows = rowsPerPageByTab[tabKey] || 6;
      const total = items.length;

      if (total === 0) {
        return {
          sliced: [],
          total: 0,
          start: 0,
          end: 0,
          totalPages: 0,
          page: 1,
          rows,
        };
      }

      const totalPages = Math.ceil(total / rows);
      const safePage = Math.min(page, totalPages);
      const start = (safePage - 1) * rows;
      const end = start + rows;

      return {
        sliced: items.slice(start, end),
        total,
        start,
        end,
        totalPages,
        page: safePage,
        rows,
      };
    },
    [pageByTab, rowsPerPageByTab],
  );

  const { instance } = useMsal();
  const dispatch = useDispatch();
  const activeAccount = instance.getActiveAccount();
  const enterpriseId = activeAccount?.idTokenClaims?.enterpriseID;

  const businessUnit = useMemo(
    () => userData?.businessUnit,
    [userData?.businessUnit],
  );

  useEffect(() => {
    if (!enterpriseId) return;

    dispatch(getUserData(enterpriseId));
  }, [enterpriseId, dispatch]);

  useEffect(() => {
    if (!businessUnit) return;

    const controller = new AbortController();
    let isMounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        setLoadError(null);

        const token = localStorage.getItem("token");
        if (!token) return;

        const headers = {
          Authorization: token,
          "Content-Type": "application/json",
        };

        const res = await fetch(`${BASE_API_URL}/getBadgeWiseData`, {
          method: "POST",
          headers,
          signal: controller.signal,
          body: JSON.stringify({ businessUnit }),
        });

        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(`${res.status} ${res.statusText} ${text}`);
        }

        const dataRaw = await res.json();

        if (!isMounted) return;

        const data = Array.isArray(dataRaw) ? dataRaw : [];

        /* ✅ Top 3 MU mapping (memo-safe) */
        if (enterpriseIds.length > 0) {
          const top3Stats = enterpriseIds.map((id) => {
            const user = data.find((u) => u.enterpriseId === id);
            return { enterpriseId: id, muSmart: user?.muSmart || "-" };
          });

          onTop3MUs?.(top3Stats);
        }

        /* ✅ Grouping optimized */
        const grouped = data.reduce((acc, user) => {
          const badge = user.highestBadge;
          if (!badge) return acc;

          (acc[badge] ||= []).push(user); // ✅ cleaner
          return acc;
        }, {});

        if (!isMounted) return;

        setDiamondLeader(grouped.Diamond || []);
        setPlatinumLeader(grouped.Platinum || []);
        setGoldLeader(grouped.Gold || []);
        setSilverLeader(grouped.Silver || []);
        setBronzeLeader(grouped.Bronze || []);
      } catch (err) {
        if (err.name === "AbortError" || !isMounted) return;

        console.error(err);
        setLoadError(err?.message || "Failed to load monthly leaders.");

        setDiamondLeader([]);
        setPlatinumLeader([]);
        setGoldLeader([]);
        setSilverLeader([]);
        setBronzeLeader([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [businessUnit, enterpriseIds, onTop3MUs]);

  useEffect(() => {
    setPageByTab((prev) => {
      const updated = { ...prev };
      TAB_KEYS.forEach((k) => (updated[k] = 1));
      return updated;
    });
  }, [
    roleFilter,
    categoryFilter,
    industryFilter,
    capabilityFilter,
    muSmartFilter,
    TAB_KEYS,
  ]);

  const handleRoleChange = (e) => setRoleFilter(e.target.value);
  const handleCategoryChange = (e) => setCategoryFilter(e.target.value);
  const handleIndustryChange = (e) => setIndustryFilter(e.target.value);
  const handleCapabilityChange = (e) => setCapabilityFilter(e.target.value);
  const handleMUSmartChange = (e) => setMuSmartFilter(e.target.value);

  const isDefaultFilter =
    roleFilter === "All" &&
    categoryFilter === "All" &&
    industryFilter === "All" &&
    capabilityFilter === "All" &&
    muSmartFilter === "All";

  const handleReset = () => {
    setRoleFilter("All");
    setCategoryFilter("All");
    setIndustryFilter("All");
    setCapabilityFilter("All");
    setMuSmartFilter("All");

    setRowsPerPageByTab(makeDefaultByTab(6));
    setPageByTab(makeDefaultByTab(1));
  };

  return (
    <>
      <div className="monthly-leaders-heading-row">
        <div className="monthly-leaders-heading-box">
          <div className="calendar-img-box">
            <img src="/Assets/HallFame/calendar-icon.png" alt="calendar icon" />
          </div>

          <div className="monthly-leaders-heading-content">
            <h3>Monthly Leaders</h3>
            <p>Top performer of each month</p>
          </div>
        </div>

        {/* Filters */}
        <div style={{ marginBottom: "2px" }}>
          <label
            style={{
              marginRight: "10px",
              fontWeight: "bold",
              color: "white",
              fontSize: "14px",
            }}
          >
            Role:
          </label>
          <div className="dropdown-selection-row">
            <select value={roleFilter} onChange={handleRoleChange}>
              <option value="All">All</option>
              <option value="Delivery">Delivery</option>
              <option value="Technology">Technology</option>
              <option value="Design">Design</option>
              <option value="Solution Arch">Solution Arch</option>
              <option value="Packaged App Development Associate">PADA</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: "2px" }}>
          <label
            style={{
              marginRight: "10px",
              fontWeight: "bold",
              color: "white",
              fontSize: "14px",
            }}
          >
            Category:
          </label>
          <div className="dropdown-selection-row">
            <select value={categoryFilter} onChange={handleCategoryChange}>
              <option value="All">All</option>
              <option value="Commerce">Commerce</option>
              <option value="Marketing">Marketing</option>
              <option value="Service">Service</option>
              <option value="DDP">DDP</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: "2px" }}>
          <label
            style={{
              marginRight: "10px",
              fontWeight: "bold",
              color: "white",
              fontSize: "14px",
            }}
          >
            Industry:
          </label>
          <div className="dropdown-selection-row">
            <select value={industryFilter} onChange={handleIndustryChange}>
              <option value="All">All</option>
              <option value="Products">Products</option>
              <option value="CMT">CMT</option>
              <option value="H&PS">H&PS</option>
              <option value="FS">FS</option>
              <option value="Resources">Resources</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: "2px" }}>
          <label
            style={{
              marginRight: "10px",
              fontWeight: "bold",
              color: "white",
              fontSize: "14px",
            }}
          >
            Capability:
          </label>
          <div className="dropdown-selection-row">
            <select value={capabilityFilter} onChange={handleCapabilityChange}>
              <option value="All">All</option>
              <option value="Adobe">Adobe</option>
              <option value="Emerging">Emerging</option>
              <option value="SAP">SAP</option>
              <option value="Salesforce">Salesforce</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: "2px" }}>
          <label
            style={{
              marginRight: "10px",
              fontWeight: "bold",
              color: "white",
              fontSize: "14px",
            }}
          >
            MuSmart:
          </label>
          <div className="dropdown-selection-row">
            <select value={muSmartFilter} onChange={handleMUSmartChange}>
              <option value="All">All</option>
              <option value="Song_PRD-AM&AP">Song_PRD-AM&AP</option>
              <option value="Song_PRD-EMEA">Song_PRD-EMEA</option>
              <option value="Song_CMT">Song_CMT</option>
              <option value="Song_FS">Song_FS</option>
              <option value="Song_Marketwide">Song_Marketwide</option>
              <option value="Song_EXP_Capabilities">
                Song_EXP_Capabilities
              </option>
              <option value="Song_RES">Song_RES</option>
              <option value="Song_H&PS">Song_H&PS</option>
              <option value="SONG_Design&Digital_Products">
                Design&Digital_Products
              </option>
              <option value="Song_Commerce">Song_Commerce</option>
              <option value="SONG_Service">Song_Service</option>
              <option value="Song_Marketing">Song_Marketing</option>
              <option value="Song_Central">Song_Central</option>
              <option value="Song_Sales&Solutions">Song_Sales&Solutions</option>
              <option value="EMEA-PRD2">EMEA-PRD2</option>
              <option value="AM - PRD - GRP2">AM - PRD - GRP2</option>
            </select>
          </div>
        </div>

        <button
          className="user-reset"
          onClick={!isDefaultFilter ? handleReset : undefined}
          style={{
            cursor: isDefaultFilter ? "not-allowed" : "pointer",
          }}
        >
          Reset
        </button>
      </div>

      <Box sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={value} onChange={handleChange} aria-label="basic tabs">
            <Tab
              label="Diamond"
              icon={
                <img
                  src="/Assets/HallFame/diamond-medal.png"
                  alt="medal"
                  className="tab-medals"
                />
              }
              iconPosition="start"
              {...a11yProps(0)}
            />
            <Tab
              label="Platinum"
              icon={
                <img
                  src="/Assets/HallFame/platinum-medal.png"
                  alt="medal"
                  className="tab-medals"
                />
              }
              iconPosition="start"
              {...a11yProps(1)}
            />
            <Tab
              label="Gold"
              icon={
                <img
                  src="/Assets/HallFame/gold-medal.png"
                  alt="medal"
                  className="tab-medals"
                />
              }
              iconPosition="start"
              {...a11yProps(2)}
            />
            <Tab
              label="Silver"
              icon={
                <img
                  src="/Assets/HallFame/silver-medal.png"
                  alt="medal"
                  className="tab-medals"
                />
              }
              iconPosition="start"
              {...a11yProps(3)}
            />
            <Tab
              label="Bronze"
              icon={
                <img
                  src="/Assets/HallFame/bronze-medal.png"
                  alt="medal"
                  className="tab-medals"
                />
              }
              iconPosition="start"
              {...a11yProps(4)}
            />
          </Tabs>
        </Box>

        <CustomTabPanel value={value} index={0}>
          <LeadersListWithPagination
            tabKey="Diamond"
            leaders={diamondLeader}
            loading={loading}
            loadError={loadError}
            roleFilter={roleFilter}
            categoryFilter={categoryFilter}
            industryFilter={industryFilter}
            capabilityFilter={capabilityFilter}
            muSmartFilter={muSmartFilter}
            getPagedData={getPagedData}
            handlePageChange={handlePageChange}
            handleRowsPerPageChange={handleRowsPerPageChange}
          />
        </CustomTabPanel>

        <CustomTabPanel value={value} index={1}>
          <LeadersListWithPagination
            tabKey="Platinum"
            leaders={platinumLeader}
            loading={loading}
            loadError={loadError}
            roleFilter={roleFilter}
            categoryFilter={categoryFilter}
            industryFilter={industryFilter}
            capabilityFilter={capabilityFilter}
            muSmartFilter={muSmartFilter}
            getPagedData={getPagedData}
            handlePageChange={handlePageChange}
            handleRowsPerPageChange={handleRowsPerPageChange}
          />
        </CustomTabPanel>

        <CustomTabPanel value={value} index={2}>
          <LeadersListWithPagination
            tabKey="Gold"
            leaders={goldLeader}
            loading={loading}
            loadError={loadError}
            roleFilter={roleFilter}
            categoryFilter={categoryFilter}
            industryFilter={industryFilter}
            capabilityFilter={capabilityFilter}
            muSmartFilter={muSmartFilter}
            getPagedData={getPagedData}
            handlePageChange={handlePageChange}
            handleRowsPerPageChange={handleRowsPerPageChange}
          />
        </CustomTabPanel>

        <CustomTabPanel value={value} index={3}>
          <LeadersListWithPagination
            tabKey="Silver"
            leaders={silverLeader}
            loading={loading}
            loadError={loadError}
            roleFilter={roleFilter}
            categoryFilter={categoryFilter}
            industryFilter={industryFilter}
            capabilityFilter={capabilityFilter}
            muSmartFilter={muSmartFilter}
            getPagedData={getPagedData}
            handlePageChange={handlePageChange}
            handleRowsPerPageChange={handleRowsPerPageChange}
          />
        </CustomTabPanel>

        <CustomTabPanel value={value} index={4}>
          <LeadersListWithPagination
            tabKey="Bronze"
            leaders={bronzeLeader}
            loading={loading}
            loadError={loadError}
            roleFilter={roleFilter}
            categoryFilter={categoryFilter}
            industryFilter={industryFilter}
            capabilityFilter={capabilityFilter}
            muSmartFilter={muSmartFilter}
            getPagedData={getPagedData}
            handlePageChange={handlePageChange}
            handleRowsPerPageChange={handleRowsPerPageChange}
          />
        </CustomTabPanel>
      </Box>
    </>
  );
}

BasicTabs.propTypes = {
  enterpriseIds: PropTypes.array,
  onTop3MUs: PropTypes.func,
};
